<template>
  <div class="shop-header" :class="rootClasses" :style="rootStyle">
    <div class="shop-header__container">
      <div class="shop-header__content">
        <ul class="shop-header__breadcrumbs">
          <li>
            <nuxt-link :to="defaultShopRoute">
              Shop
            </nuxt-link>
          </li>
          <li class="shop-header__breadcrumb shop-header__breadcrumb--capitalise">
            &roarr;
            {{ translatedActiveTree }}
          </li>
          <li v-for="breadcrumb in breadcrumbs" :key="breadcrumb.uri">
            &roarr;
            <nuxt-link :to="breadcrumb.uri" v-html="breadcrumb.name" />
          </li>
        </ul>

        <h1 class="shop-header__headline" v-html="pageMeta?.name" />

        <div class="shop-header__description" v-html="pageMeta?.description" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defaultShopRoute } from "~/common/ts/storeHelpers";
import { useSearchStore } from "~/stores/searchStore";

export default {
  setup () {
    return {
      searchStore: useSearchStore(),
    };
  },
  props: {
    activeTree: String,
  },
  computed: {
    activeFilters () {
      return this.searchStore.urlFilters.categories;
    },
    pageMeta () {
      return this.searchStore.data.currentPage;
    },
    defaultShopRoute () {
      return defaultShopRoute;
    },
    translatedActiveTree () {
      let translation = this.activeTree;

      if (this.activeTree === 'sound-designer') {
        translation = 'Sound Designer';
      } else if (this.activeTree === 'categories') {
        translation = 'Categories';
      }

      return translation;
    },
    breadcrumbs () {
      const response = [];
      let baseUri = `/shop/${this.activeTree}`;

      if (this.activeTree === 'category') {
        this.activeFilters?.forEach((categorySlug) => {
          const filter = this.searchStore.data.categories.find((categoryData) => categoryData.slug === categorySlug);

          if (filter) {
            response.push({
              ...filter,
              uri: `${baseUri}/${filter.slug}`,
            });

            baseUri += `/${filter.slug}`;
          }
        });
      } else if (this.pageMeta) {
        response.push({
          name: this.pageMeta.name,
          uri: `${baseUri}/${this.pageMeta.slug}`,
        })
      }

      return response;
    },
    rootClasses () {
      // trying to check if first element is a tag that probably contains an image
      const hasOpeningTag = this.pageMeta?.description?.startsWith('<');

      return {
        'shop-header--custom-background': this.activeTree !== 'sound-designer' && !!this.pageMeta?.image?.mediaItemUrl,
        'shop-header--opening-tag': hasOpeningTag,
      };
    },
    rootStyle () {
      if (this.activeTree === 'sound-designer') {
        return null
      }
      return {
        '--background-image': this.pageMeta?.image?.mediaItemUrl
          ? `url(${this.$img(this.pageMeta.image.mediaItemUrl, { blur: 2, width: 540, height: 300 })})`
          : null,
      }
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/variables";
@use "~/common/scss/typography";
@use "~/common/scss/mq";

.shop-header {
  $root: &;

  background: variables.$colorGreyED;
  color: variables.$colorBlack;
  min-height: helpers.px-to-rem(300);
  transition: color .25s;
  width: 100%;
  @include mq.mobileTablet() {
    min-height: 0;
  }

  &--custom-background {
    background: var(--background-image, #333) no-repeat 50% 50%;
    background-size: cover;
    color: #FFF;
  }

  &__container {
    height: 100%;
    width: 100%;

    #{$root}--custom-background & {
      background-color: #00000055;
    }
  }

  &__content {
    margin: 0 auto;
    max-width: variables.$containerLarge;
    min-height: helpers.px-to-rem(300);
    padding: 2rem 0 4rem;
    width: 95%;
    @include mq.mobileTablet() {
      min-height: 0;
      padding: 2rem 0;
    }
  }

  &__breadcrumbs {
    align-items: center;
    display: flex;
    gap: .25rem;
    list-style: none;
    margin-bottom: 1rem;

    a {
      color: currentColor;
    }
  }

  &__breadcrumb {

    &--capitalise {
      text-transform: capitalize;
    }
  }

  &__headline {
    @include typography.h3();

    color: currentColor;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  &__description {
    font-size: helpers.px-to-rem(16);
    max-width: variables.$containerMediumSmall;

    img {
      object-fit: contain;
      margin: 0 1rem 1rem 0;
      max-height: helpers.px-to-rem(150);
      width: auto;
    }

    a {
      color: currentColor;
    }

    #{$root}--opening-tag & > *:first-child {
      float: left;
    }
  }
}
</style>
